import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  BELÉPÉSI FIÓK A TÖRZSADAT-SZERKESZTŐBŐL
 *  ===========================================================================
 *
 *  A rendszer két nyilvántartásból áll: a törzsadatból (ki a dolgozó, a tanuló, a
 *  gondviselő) és a belépési rendszerből (kinek van fiókja). A kettő eddig csak a
 *  tanév eleji tömeges importon keresztül találkozott – egyetlen új kolléga vagy
 *  egyetlen frissen beiratkozott tanuló gondviselője miatt viszont senki nem akar
 *  importfájlt gyártani és a Keycloak admin felületére belépni. Ezért az a néhány
 *  művelet, ami ilyenkor kell, itt van a szerkesztőben.
 *
 *  HÁROM SZABÁLY, AMI NEM VÁLTOZIK:
 *
 *   1. Magától semmi nem történik. A fiók akkor jön létre, ha rákattintanak.
 *   2. Meglévő fiókot nem írunk felül kérdés nélkül – a felülírás kizárja a
 *      felhasználót a régi jelszavával, ez tudatos döntés.
 *   3. A jelszót NEM tároljuk. Amit a létrehozás visszaad, azt ez a képernyő
 *      tartja, amíg nyitva van; utána már csak új jelszót lehet küldeni, és ezt
 *      ki is mondjuk, mert a felírt régi adatok ettől érvénytelenné válnak.
 */
@Component({
  selector: 'app-belepes-fiok',
  templateUrl: './belepes-fiok.component.html',
  styleUrls: ['./belepes-fiok.component.scss'],
})
export class BelepesFiokComponent implements OnInit, OnChanges {
  /** Melyik körbe tartozik: ez adja a Keycloak-csoportot és a jogkört is. */
  @Input() fajta: 'dolgozo' | 'gondviselo' | 'tanulo' = 'dolgozo';
  /** A törzsadatbeli azonosító (dolgozó/tanuló: adid, gondviselő: username). */
  @Input() azonosito = '';
  /** Csak kiíráshoz – a kiszolgáló a saját törzsadatából dolgozik. */
  @Input() nev = '';

  /*  Az intézmény a JOGOSULTSÁGHOZ kell: a kiszolgáló ehhez méri, hogy a kérő
   *  hozzányúlhat-e valaki belépéséhez. A szerkesztőknek nem kell átadniuk – a
   *  belépett felhasználó intézménye ugyanaz. */
  intezmeny = '';
  allapot: any = null;
  toltodik = false;
  fut = false;
  hiba = '';
  /** A most kiadott belépési adatok – csak amíg a szerkesztő nyitva van. */
  frissAdat: { username: string; jelszo: string; jelszo_forras: string } | null = null;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const sajat = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(sajat?.intezmenyId || '');
    if (this.azonosito && !this.allapot) this.betolt();
  }

  ngOnChanges(valtozas: SimpleChanges) {
    if (valtozas['azonosito'] || valtozas['fajta']) {
      this.frissAdat = null;
      this.betolt();
    }
  }

  private ut(vegpont: string): string {
    return `/api/admin/keycloak-fiokok/${vegpont}/${this.fajta}/${encodeURIComponent(this.azonosito)}`;
  }

  async betolt() {
    this.allapot = null;
    this.hiba = '';
    if (!this.azonosito) return;
    if (!this.intezmeny) return;   // az ngOnInit után újra lefut
    this.toltodik = true;
    try {
      const v: any = await firstValueFrom(this.http.get(
        `${this.ut('fiok')}?intezmeny=${encodeURIComponent(this.intezmeny)}`));
      if (v?.hiba) this.hiba = v.hiba; else this.allapot = v;
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A belépési rendszer most nem érhető el.';
    } finally {
      this.toltodik = false;
    }
  }

  get vanFiok(): boolean { return !!this.allapot?.van; }

  /** A fiók MÁS néven van (e-mail alapján találtuk meg) – ezt ki kell mondani. */
  get masNeven(): boolean {
    return this.vanFiok && this.allapot?.egyezes === 'email';
  }

  async fiokot(felulir = false, kuldes = false) {
    if (!this.azonosito || this.fut) return;
    this.fut = true;
    try {
      const v: any = await firstValueFrom(
        this.http.post(this.ut('fiok'), { felulir, kuldes, intezmeny: this.intezmeny }));
      this.frissAdat = { username: v.username, jelszo: v.jelszo, jelszo_forras: v.jelszo_forras };
      const mit = v.felulirva ? 'Új jelszó beállítva' : 'Fiók létrehozva';
      this.data.sendToast(v.level ? `${mit}, a levél elment: ${v.level}` : `${mit}.`, 'success');
      if (v.level_hiba) this.data.sendToast(v.level_hiba, 'warning');
      await this.betolt();
    } catch (e: any) {
      /*  A 409 nem hiba, hanem VÁLASZ: van már fiók. Itt kérdezünk rá, hogy a
       *  felülírás tudatos döntés legyen, és ne egy második kattintás mellékhatása. */
      if (e?.status === 409 && e?.error?.letezik) {
        this.fut = false;
        if (confirm(`${e.error.error}\n\nBiztosan felülírod?`)) return this.fiokot(true, kuldes);
        return;
      }
      this.data.sendToast(e?.error?.error || 'A fiók létrehozása nem sikerült.', 'error');
    } finally {
      this.fut = false;
    }
  }

  /** A most kiadott adatok kiküldése; ha már nincsenek meg, új jelszóval. */
  async kikuldes() {
    if (!this.azonosito || this.fut) return;
    this.fut = true;
    try {
      const v: any = await firstValueFrom(this.http.post(this.ut('fiok-email'),
        { jelszo: this.frissAdat?.jelszo || '', intezmeny: this.intezmeny }));
      this.data.sendToast(`A belépési adatok elmentek: ${v.level}`, 'success');
    } catch (e: any) {
      if (e?.status === 400 && e?.error?.ujjelszo_kell) {
        this.fut = false;
        if (!confirm(`${e.error.error}\n\nMehet az új jelszó?`)) return;
        this.fut = true;
        try {
          const v: any = await firstValueFrom(this.http.post(this.ut('fiok-email'),
            { ujjelszo: true, intezmeny: this.intezmeny }));
          this.frissAdat = { username: this.allapot?.kc_username || this.azonosito,
                             jelszo: v.jelszo, jelszo_forras: v.jelszo_forras };
          this.data.sendToast(`Új jelszó és belépési adatok elmentek: ${v.level}`, 'success');
        } catch (e2: any) {
          this.data.sendToast(e2?.error?.error || 'A kiküldés nem sikerült.', 'error');
        } finally { this.fut = false; }
        return;
      }
      this.data.sendToast(e?.error?.error || 'A kiküldés nem sikerült.', 'error');
    } finally {
      this.fut = false;
    }
  }

  masol(mit: string) {
    try {
      navigator.clipboard.writeText(mit);
      this.data.sendToast('Vágólapra másolva.', 'success');
    } catch {
      this.data.sendToast('A másolás nem sikerült – jelöld ki és másold kézzel.', 'warning');
    }
  }
}
